import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSION_MODULES as PM } from '../constants/permissionModules';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { ReportStatusBadge, SaveState, SectionStateDot, SectionCard, BlockedState } from '../components/devreport/shared';
import {
  OverviewSection, FootballSection, MatchAnalysisSection, PhysicalGpsSection, FitnessSection,
  BiometricsSection, AttendanceSection, MentalSection, PdpSection, InjurySection,
} from '../components/devreport/sections';
import { SummaryEditor, StrengthsEditor, PrioritiesEditor } from '../components/devreport/editors';
import {
  SectionConfigBar, StaffAssessmentEditor, FootballAreasEditor, ManualTargetsEditor,
  CustomSectionsEditor, MAJOR_ASSESSMENT_SECTIONS, MANUAL_TARGET_SECTIONS,
  SECTION_EVIDENCE_SECTIONS, defaultConfig,
} from '../components/devreport/authoring';
import { ReportEvidenceEditor } from '../components/devreport/ReportEvidenceEditor';
import { PhysicalGpsAnalysisNotesEditor } from '../components/devreport/PhysicalGpsAnalysisNotesEditor';
import { ShareReportDialog } from '../components/devreport/ShareReportDialog';
import ReportPreview from '../components/devreport/ReportPreview';
import { ReportPresentationEditor, ConciseSummaryEditor } from '../components/devreport/ReportPresentationEditor';
import { BUILDER_ORDER, SECTION_LABELS, SECTION_SHORT, fmtDateTime } from '../components/devreport/constants';
import { ArrowLeft, RefreshCw, Eye, PenLine, Lock, AlertTriangle, FileText, CalendarRange, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Build the pool of evidence a staff member may attach to strengths/priorities —
// only from ASSEMBLED, authorized sections (never a backdoor into source modules).
function buildAvailableEvidence(assembled) {
  const out = [];
  const ma = assembled?.match_analysis;
  if (ma?.has_data) (ma.data.observations || []).forEach((o) => out.push({
    source_type: o.evidence_url ? 'video_evidence' : 'match_observation',
    source_id: o.id,
    label: (o.text ? o.text.slice(0, 40) : 'Observation') + (o.timestamp ? ` @${o.timestamp}` : ''),
  }));
  const fit = assembled?.fitness;
  if (fit?.has_data) (fit.data.tests || []).forEach((t) => out.push({ source_type: 'fitness', source_id: t.test_type, label: `Fitness · ${t.test_type}` }));
  const gps = assembled?.physical_gps;
  if (gps?.has_data) (gps.data.match_by_match || []).forEach((m) => out.push({ source_type: 'gps', source_id: m.gps_activity_id || m.activity_date, label: `GPS · ${m.activity_date}` }));
  const pdp = assembled?.pdp;
  if (pdp?.has_data) (pdp.data.goals || []).forEach((g) => out.push({ source_type: 'pdp', source_id: g.id, label: `PDP · ${g.title}` }));
  const bio = assembled?.biometrics;
  if (bio?.has_data && bio.data.latest?.date) out.push({ source_type: 'biometrics', source_id: bio.data.latest.date, label: `Biometrics · ${bio.data.latest.date}` });
  return out;
}

const DevelopmentReportBuilder = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { user, hasModuleAccess } = useAuth();
  const isAdmin = user?.role === 'Admin';
  const canEdit = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS, 'edit');
  const canFinalize = isAdmin || hasModuleAccess('development_reports.finalize', 'edit');
  const canShare = isAdmin || hasModuleAccess(PM.DEVELOPMENT_REPORTS_SHARE, 'edit');

  const [report, setReport] = useState(null);
  const [assembled, setAssembled] = useState({});
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState('overview');
  const [previewMode, setPreviewMode] = useState(false);
  const [saveState, setSaveState] = useState('saved');
  const [refreshing, setRefreshing] = useState(false);

  // Report-owned qualitative state (local, persisted explicitly / on blur).
  const [summary, setSummary] = useState('');
  const [strengths, setStrengths] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [selectedEvidence, setSelectedEvidence] = useState([]);
  // Phase 92C.1 — report-owned manual/hybrid content.
  const [staffAssessments, setStaffAssessments] = useState({});
  const [sectionConfig, setSectionConfig] = useState({});
  const [footballAreas, setFootballAreas] = useState([]);
  const [manualTargets, setManualTargets] = useState([]);
  const [customSections, setCustomSections] = useState([]);
  // Phase 92E.1 — per-section evidence builder (currently used by GPS & Mental).
  const [sectionEvidence, setSectionEvidence] = useState({});
  const [physicalGpsAnalysisNotes, setPhysicalGpsAnalysisNotes] = useState([]);

  const [showFinalize, setShowFinalize] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  // Phase 92E.1 (gap fix) — Edit Review Period dialog + post-save refresh prompt.
  const [showEditPeriod, setShowEditPeriod] = useState(false);
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [savingPeriod, setSavingPeriod] = useState(false);
  const [showRefreshPrompt, setShowRefreshPrompt] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/development-reports/${reportId}`);
      const rep = res.data.report;
      if (rep.status !== 'draft') { navigate(`/development-reports/${reportId}`, { replace: true }); return; }
      setReport(rep);
      setAssembled(res.data.assembled || {});
      setSummary(rep.development_summary || '');
      setStrengths(rep.strengths || []);
      setPriorities(rep.priorities || []);
      setSelectedEvidence(rep.selected_evidence || []);
      setStaffAssessments(rep.staff_assessments || {});
      setSectionConfig(rep.section_config || {});
      setFootballAreas(rep.football_areas || []);
      setManualTargets(rep.manual_targets || []);
      setCustomSections(rep.custom_sections || []);
      setSectionEvidence(rep.section_evidence || {});
      setPhysicalGpsAnalysisNotes(rep.physical_gps_analysis_notes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
      navigate('/development-reports');
    } finally {
      setLoading(false);
    }
  }, [reportId, navigate]);

  useEffect(() => { load(); }, [load]);

  // Unsaved-changes guard.
  useEffect(() => {
    const h = (e) => { if (saveState === 'unsaved') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [saveState]);

  const persist = useCallback(async (path, body) => {
    setSaveState('saving');
    try {
      await axios.put(`${API}/development-reports/${reportId}${path}`, body);
      setSaveState('saved');
      return true;
    } catch (e) {
      setSaveState('error');
      toast.error(e.response?.data?.detail || 'Save failed — your latest edit was not stored');
      return false;
    }
  }, [reportId]);

  const availableEvidence = useMemo(() => buildAvailableEvidence(assembled), [assembled]);
  const includedIds = useMemo(() => new Set(selectedEvidence.map((e) => e.source_id)), [selectedEvidence]);

  const persistAuthoring = useCallback((patch) => persist('/authoring', patch), [persist]);
  const cfgFor = (k) => ({ ...defaultConfig(), ...(sectionConfig[k] || {}) });
  const setCfg = (k, cfg) => { const next = { ...sectionConfig, [k]: cfg }; setSectionConfig(next); persistAuthoring({ section_config: next }); };
  const setSectionEvidenceFor = (k, nextItems) => {
    const next = { ...sectionEvidence, [k]: nextItems };
    setSectionEvidence(next);
    setSaveState('unsaved');
    persistAuthoring({ section_evidence: { [k]: nextItems } });
  };

  const sectionsToShow = useMemo(() => [...BUILDER_ORDER.filter((k) => (report?.selected_sections || []).includes(k)), 'custom'], [report]);

  const sectionState = (key) => {
    if (key === 'development_summary') return summary?.trim() ? 'authored' : 'no_data';
    if (key === 'custom') return customSections.length ? 'authored' : 'no_data';
    const s = assembled[key];
    const authored = !!(staffAssessments[key]?.trim());
    if (!s) return authored ? 'authored' : 'no_data';
    if (s.allowed === false) return authored ? 'authored' : 'blocked';
    if (s.has_data) return 'has_data';
    return authored ? 'authored' : 'no_data';
  };

  // ── Evidence include/exclude/reorder (structural — deliberate save) ──────────
  const toggleObservation = async (o) => {
    let next;
    if (includedIds.has(o.id)) {
      next = selectedEvidence.filter((e) => e.source_id !== o.id);
    } else {
      next = [...selectedEvidence, {
        source_type: o.evidence_url ? 'video_evidence' : 'match_observation',
        source_id: o.id,
        label: (o.text ? o.text.slice(0, 60) : 'Observation') + (o.timestamp ? ` @${o.timestamp}` : ''),
        snapshot_display_data: o,
        order: selectedEvidence.length,
      }];
    }
    next = next.map((e, i) => ({ ...e, order: i }));
    setSelectedEvidence(next);
    await persist('/evidence', { selected_evidence: next });
  };

  const moveEvidence = async (sourceId, dir) => {
    const idx = selectedEvidence.findIndex((e) => e.source_id === sourceId);
    const j = idx + dir; if (j < 0 || j >= selectedEvidence.length) return;
    const next = [...selectedEvidence]; [next[idx], next[j]] = [next[j], next[idx]];
    const reordered = next.map((e, i) => ({ ...e, order: i }));
    setSelectedEvidence(reordered);
    await persist('/evidence', { selected_evidence: reordered });
  };

  // ── Source refresh ───────────────────────────────────────────────────────────
  // Phase 92E.1 (gap fix) — NEVER silently drop selected_evidence just because
  // its source fell outside the new period. The backend already preserves it;
  // the frontend used to strip it here, with no way to review/keep it. Now we
  // only recompute Hub Data and flag out-of-period items (see
  // `outsidePeriodEvidence` below) for staff to explicitly keep or remove.
  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await axios.post(`${API}/development-reports/${reportId}/refresh`);
      const newAssembled = res.data.assembled || {};
      setAssembled(newAssembled);
      setReport((r) => ({ ...r, last_refreshed_at: new Date().toISOString() }));
      const newObsIds = new Set((newAssembled.match_analysis?.data?.observations || []).map((o) => o.id));
      const nowOutside = selectedEvidence.filter((e) => ['match_observation', 'video_evidence'].includes(e.source_type) && !newObsIds.has(e.source_id));
      setShowRefreshPrompt(false);
      toast.success('Hub Data refreshed for the current review period. Your summary, strengths, priorities and evidence selections were preserved.');
      if (nowOutside.length > 0) {
        toast.info(`${nowOutside.length} selected evidence item${nowOutside.length > 1 ? 's' : ''} now fall${nowOutside.length > 1 ? '' : 's'} outside the review period — see Match Analysis to keep or remove.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const openEditPeriod = () => {
    setPeriodStart(report.review_start_date || '');
    setPeriodEnd(report.review_end_date || '');
    setShowEditPeriod(true);
  };

  const periodInvalid = periodStart && periodEnd && periodStart > periodEnd;
  const savePeriod = async () => {
    if (!periodStart || !periodEnd || periodInvalid) {
      toast.error('Start date must be on or before the end date');
      return;
    }
    setSavingPeriod(true);
    try {
      const res = await axios.put(`${API}/development-reports/${reportId}`, {
        review_start_date: periodStart, review_end_date: periodEnd,
      });
      setReport((r) => ({ ...r, review_start_date: res.data.review_start_date, review_end_date: res.data.review_end_date }));
      setShowEditPeriod(false);
      toast.success('Review period updated. Your authored content was not changed.');
      setShowRefreshPrompt(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update review period');
    } finally {
      setSavingPeriod(false);
    }
  };

  // Previously selected match evidence whose source no longer falls inside the
  // CURRENT review period. Stays selected (per requirement) until staff
  // explicitly removes it — never auto-dropped.
  const outsidePeriodEvidence = useMemo(() => {
    const inPeriodIds = new Set((assembled?.match_analysis?.data?.observations || []).map((o) => o.id));
    return selectedEvidence.filter((e) => ['match_observation', 'video_evidence'].includes(e.source_type) && !inPeriodIds.has(e.source_id));
  }, [selectedEvidence, assembled]);

  const keepOutsidePeriodEvidence = () => toast.success('Kept — still included in the report.');
  const removeOutsidePeriodEvidence = async (sourceId) => {
    const next = selectedEvidence.filter((e) => e.source_id !== sourceId).map((e, i) => ({ ...e, order: i }));
    setSelectedEvidence(next);
    await persist('/evidence', { selected_evidence: next });
    toast.success('Removed from report');
  };

  const finalize = async () => {
    setFinalizing(true);
    try {
      await axios.post(`${API}/development-reports/${reportId}/finalize`);
      toast.success('Report finalized');
      navigate(`/development-reports/${reportId}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Finalization failed.';
      if (e.response?.status === 403) msg = typeof detail === 'string' && detail.includes('unauthorised') ? 'Cannot finalize — the report includes sections you are not permitted to assemble.' : 'You do not have permission to finalize this report.';
      else if (typeof detail === 'string') msg = detail;
      toast.error(msg);
      setShowFinalize(false);
    } finally {
      setFinalizing(false);
    }
  };

  const goBack = () => {
    if (saveState === 'unsaved' && !window.confirm('You have unsaved changes. Leave anyway?')) return;
    navigate('/development-reports');
  };

  if (loading) return <Layout><div className="py-16 text-center text-slate-400">Loading builder…</div></Layout>;
  if (!report) return null;

  const evidenceIncludedCount = selectedEvidence.length;

  // ── Center content ───────────────────────────────────────────────────────────
  const renderSection = () => {
    const s = assembled[active];
    if (active === 'development_summary') {
      return (
        <div className="space-y-6">
          <SectionCard title="Development Summary" subtitle="Your concise professional overview of the review period." testId="builder-summary">
            <SummaryEditor value={summary} onChange={(v) => { setSummary(v); setSaveState('unsaved'); }} onCommit={() => persist('/summary', { development_summary: summary })} />
          </SectionCard>
          <SectionCard title="Key Strengths" subtitle="Recommended: 2–4." testId="builder-strengths">
            <StrengthsEditor items={strengths} setItems={(v) => { setStrengths(v); setSaveState('unsaved'); }} onCommit={(next) => persist('/strengths', { strengths: next || strengths })} availableEvidence={availableEvidence} />
          </SectionCard>
          <SectionCard title="Priority Development Areas" subtitle="Recommended: 3–5." testId="builder-priorities">
            <PrioritiesEditor items={priorities} setItems={(v) => { setPriorities(v); setSaveState('unsaved'); }} onCommit={(next) => persist('/priorities', { priorities: next || priorities })} availableEvidence={availableEvidence} reportId={reportId} />
          </SectionCard>
          <SectionCard title="Report Presentation" subtitle="Cover photo, lead reviewer and the closing Next-Review-Period message." testId="builder-presentation">
            <ReportPresentationEditor reportId={reportId} report={report} onSaved={() => setSaveState('saved')} />
          </SectionCard>
        </div>
      );
    }
    if (active === 'custom') {
      return (
        <SectionCard title="Custom Sections" subtitle="Report-owned content not covered by the standard template." testId="builder-custom">
          <CustomSectionsEditor items={customSections} setItems={(v) => { setCustomSections(v); setSaveState('unsaved'); }} onCommit={(next) => persistAuthoring({ custom_sections: next || customSections })} />
        </SectionCard>
      );
    }
    const isMajor = MAJOR_ASSESSMENT_SECTIONS.includes(active);
    const blockedHub = s?.allowed === false;
    const content = (() => {
      if (blockedHub) return <BlockedState message={s.message} />;
      switch (active) {
        case 'overview': return <OverviewSection data={s?.data} meta={{ author: report.created_by_name }} />;
        case 'football_performance': return <FootballSection data={s?.data} />;
        case 'match_analysis': return <MatchAnalysisSection data={s?.data} included={includedIds} onToggle={toggleObservation} onMove={moveEvidence} />;
        case 'physical_gps': return <PhysicalGpsSection data={s?.data} />;
        case 'fitness': return <FitnessSection data={s?.data} />;
        case 'biometrics': return <BiometricsSection data={s?.data} />;
        case 'attendance': return <AttendanceSection data={s?.data} />;
        case 'mental': return <MentalSection data={s?.data} />;
        case 'pdp': return <PdpSection data={s?.data} />;
        case 'injury_rehab': return <InjurySection data={s?.data} />;
        default: return null;
      }
    })();
    return (
      <SectionCard title={SECTION_LABELS[active]} testId={`builder-${active}`}>
        {isMajor && <SectionConfigBar config={cfgFor(active)} onChange={(cfg) => setCfg(active, cfg)} hasData={!!s?.has_data} />}
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-400">Hub Data {blockedHub ? '(no permission)' : (s?.has_data ? '' : '(none)')}</div>
        {content}
        {active === 'match_analysis' && outsidePeriodEvidence.length > 0 && (
          <div className="mt-4 space-y-2" data-testid="outside-period-evidence">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-amber-600">
              <AlertTriangle className="h-3.5 w-3.5" /> Previously Selected Evidence — Outside Review Period
            </div>
            <p className="text-xs text-slate-500">
              Selected earlier, but its date now falls outside {report.review_start_date} – {report.review_end_date}.
              It stays included in the report until you remove it.
            </p>
            {outsidePeriodEvidence.map((e) => {
              const snap = e.snapshot_display_data || {};
              return (
                <div key={e.source_id} className="rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5" data-testid={`outside-period-item-${e.source_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[11px] font-medium text-amber-900" data-testid={`outside-period-badge-${e.source_id}`}>Outside Review Period</span>
                        <span className="text-xs text-slate-400">{e.source_type === 'video_evidence' ? 'Video Evidence' : 'Match Observation'}</span>
                        {snap.observation_date && <span className="text-xs text-slate-400">· {snap.observation_date}</span>}
                        {snap.timestamp && <span className="text-xs text-slate-400">@{snap.timestamp}</span>}
                      </div>
                      <p className="text-sm text-slate-700">{e.label || snap.text || 'Selected evidence'}</p>
                      {snap.evidence_url && (
                        <a href={snap.evidence_url} target="_blank" rel="noreferrer" className="text-xs font-medium text-sky-600 hover:underline" data-testid={`outside-period-link-${e.source_id}`}>View evidence</a>
                      )}
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => keepOutsidePeriodEvidence()} data-testid={`keep-outside-${e.source_id}`}>Keep in Report</Button>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50" onClick={() => removeOutsidePeriodEvidence(e.source_id)} data-testid={`remove-outside-${e.source_id}`}>Remove from Report</Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {active === 'football_performance' && (
          <FootballAreasEditor items={footballAreas} setItems={(v) => { setFootballAreas(v); setSaveState('unsaved'); }} onCommit={(next) => persistAuthoring({ football_areas: next || footballAreas })} reportId={reportId} />
        )}
        {MANUAL_TARGET_SECTIONS.includes(active) && (
          <ManualTargetsEditor sectionKey={active} items={manualTargets} setItems={(v) => { setManualTargets(v); setSaveState('unsaved'); }} onCommit={(next) => persistAuthoring({ manual_targets: next || manualTargets })} />
        )}
        {SECTION_EVIDENCE_SECTIONS.includes(active) && (
          <div className="mt-4">
            <div className="mb-1 text-sm font-medium text-slate-700">Section Evidence</div>
            <ReportEvidenceEditor items={sectionEvidence[active] || []} reportId={reportId} onChange={(next) => setSectionEvidenceFor(active, next)} testId={`section-evidence-${active}`} />
          </div>
        )}
        {active === 'physical_gps' && (
          <PhysicalGpsAnalysisNotesEditor
            items={physicalGpsAnalysisNotes}
            evidence={sectionEvidence.physical_gps || []}
            setItems={(next) => { setPhysicalGpsAnalysisNotes(next); setSaveState('unsaved'); }}
            onCommit={(next) => persistAuthoring({ physical_gps_analysis_notes: next || physicalGpsAnalysisNotes })}
          />
        )}
        {isMajor && (
          <StaffAssessmentEditor
            value={staffAssessments[active]}
            onChange={(v) => { setStaffAssessments((p) => ({ ...p, [active]: v })); setSaveState('unsaved'); }}
            onCommit={() => persistAuthoring({ staff_assessments: staffAssessments })}
          />
        )}
        {isMajor && (
          <ConciseSummaryEditor reportId={reportId} sectionKey={active} initial={(report.section_summaries || {})[active]} onSaved={() => setSaveState('saved')} />
        )}
      </SectionCard>
    );
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-5" data-testid="report-builder">
        {/* Header */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={goBack} className="text-slate-400 hover:text-slate-700" data-testid="builder-back"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-lg font-semibold text-slate-800">{report.athlete_name}</h1>
              <div className="text-xs text-slate-400">{report.report_title}</div>
            </div>
            <ReportStatusBadge status={report.status} />
            <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-500">
              <CalendarRange className="h-3.5 w-3.5" />
              <span data-testid="builder-review-period">{report.review_start_date} – {report.review_end_date}</span>
              <button onClick={openEditPeriod} className="ml-1 font-medium text-emerald-700 hover:underline" data-testid="open-edit-period">Edit Review Period</button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <SaveState state={saveState} />
            <Button variant="outline" size="sm" onClick={() => setPreviewMode((v) => !v)} data-testid="toggle-preview">
              {previewMode ? <><PenLine className="mr-1.5 h-4 w-4" /> Back to Builder</> : <><Eye className="mr-1.5 h-4 w-4" /> Quick Preview</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/development-reports/${reportId}/preview`)} data-testid="open-final-preview">
              <FileText className="mr-1.5 h-4 w-4" /> Final Document Preview
            </Button>
            {canShare && <ShareReportDialog reportId={reportId} />}
            {canFinalize && (
              <Button size="sm" onClick={() => setShowFinalize(true)} data-testid="open-finalize"><Lock className="mr-1.5 h-4 w-4" /> Finalize</Button>
            )}
          </div>
        </div>

        {showRefreshPrompt && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3" data-testid="refresh-prompt-banner">
            <div className="text-sm text-amber-800">
              <span className="font-medium">Review period updated.</span> That only changed metadata — GPS, Fitness, Mental, Match Analysis and other source-backed sections still reflect the old period until you refresh.
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={refresh} disabled={refreshing} data-testid="refresh-prompt-btn">
                <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh Hub Data for New Period
              </Button>
              <button onClick={() => setShowRefreshPrompt(false)} className="text-amber-500 hover:text-amber-700" data-testid="dismiss-refresh-prompt"><X className="h-4 w-4" /></button>
            </div>
          </div>
        )}

        {previewMode ? (
          <div className="rounded-2xl bg-slate-50 p-4 sm:p-6">
            <ReportPreview report={{ ...report, development_summary: summary, strengths, priorities, selected_evidence: selectedEvidence, staff_assessments: staffAssessments, section_config: sectionConfig, football_areas: footballAreas, manual_targets: manualTargets, custom_sections: customSections, section_evidence: sectionEvidence, physical_gps_analysis_notes: physicalGpsAnalysisNotes }} sections={assembled} draft />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-5">
            {/* Left nav */}
            <nav className="lg:sticky lg:top-4 self-start" data-testid="builder-nav">
              <div className="rounded-2xl border border-slate-200 bg-white p-2">
                {sectionsToShow.map((key) => (
                  <button
                    key={key}
                    onClick={() => setActive(key)}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${active === key ? 'bg-emerald-50 font-medium text-emerald-800' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`nav-${key}`}
                  >
                    <SectionStateDot state={sectionState(key)} />
                    <span className="truncate">{SECTION_SHORT[key]}</span>
                  </button>
                ))}
              </div>
            </nav>

            {/* Center */}
            <div className="min-w-0" data-testid="builder-workspace">{renderSection()}</div>

            {/* Right context / completeness */}
            <aside className="lg:sticky lg:top-4 self-start space-y-4" data-testid="builder-context">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-2 text-sm font-semibold text-slate-700">Source Data</div>
                <div className="text-xs text-slate-500">Refreshed: {report.last_refreshed_at ? fmtDateTime(report.last_refreshed_at) : 'at creation'}</div>
                <Button variant="outline" size="sm" className="mt-2 w-full" onClick={refresh} disabled={refreshing} data-testid="refresh-source">
                  <RefreshCw className={`mr-1.5 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh Source Data
                </Button>
                <p className="mt-1.5 text-[11px] text-slate-400">Recalculates GPS/Fitness/Mental/Match Analysis etc. for the current dates. Summary, Strengths, Priorities, Targets and evidence selections are preserved (evidence outside the period is flagged, never auto-removed).</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="mb-2 text-sm font-semibold text-slate-700">Report Completeness</div>
                <ul className="space-y-1.5 text-sm">
                  <Check label="Review period set" done />
                  <Check label="Development Summary" done={!!summary.trim()} />
                  <Check label="Evidence selected" done={evidenceIncludedCount > 0} note={`${evidenceIncludedCount} included`} />
                  <Check label="Key Strengths" done={strengths.length > 0} note={`${strengths.length}`} />
                  <Check label="Priority Development Areas" done={priorities.length > 0} note={`${priorities.length}`} />
                </ul>
              </div>
            </aside>
          </div>
        )}
      </div>

      {/* Finalize confirmation */}
      <Dialog open={showFinalize} onOpenChange={setShowFinalize}>
        <DialogContent data-testid="finalize-dialog">
          <DialogHeader><DialogTitle>Finalize Development Report</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1 text-sm">
            <Summary label="Athlete" value={report.athlete_name} />
            <Summary label="Review Period" value={`${report.review_start_date} – ${report.review_end_date}`} />
            <Summary label="Sections" value={`${sectionsToShow.length} selected`} />
            <Summary label="Key Strengths" value={`${strengths.length}`} />
            <Summary label="Priorities" value={`${priorities.length}`} />
            <Summary label="Included Evidence" value={`${evidenceIncludedCount}`} />
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Finalizing creates a locked historical snapshot. The report cannot be edited or refreshed afterward.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFinalize(false)} disabled={finalizing}>Cancel</Button>
            <Button onClick={finalize} disabled={finalizing} data-testid="confirm-finalize">{finalizing ? 'Finalizing…' : 'Finalize Report'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 92E.1 (gap fix) — Edit Review Period */}
      <Dialog open={showEditPeriod} onOpenChange={setShowEditPeriod}>
        <DialogContent data-testid="edit-period-dialog">
          <DialogHeader><DialogTitle>Edit Review Period</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Review Start Date</label>
              <Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="edit-period-start" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">Review End Date</label>
              <Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="edit-period-end" />
            </div>
            {periodInvalid && (
              <p className="text-xs text-rose-600" data-testid="edit-period-error">Start date must be on or before the end date.</p>
            )}
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-800">
              This only updates the report's dates — your Summary, Strengths, Priorities, Staff Assessments, Targets and Evidence are never touched. Afterward, use <strong>Refresh Hub Data for New Period</strong> to recalculate source-backed content (GPS, Fitness, Mental, Match Analysis, etc.) for the new dates.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditPeriod(false)} disabled={savingPeriod}>Cancel</Button>
            <Button onClick={savePeriod} disabled={savingPeriod || !periodStart || !periodEnd || periodInvalid} data-testid="save-period">{savingPeriod ? 'Saving…' : 'Save Review Period'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

const Check = ({ label, done, note }) => (
  <li className="flex items-center gap-2">
    <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${done ? 'bg-emerald-500 text-white' : 'border border-slate-300 text-transparent'}`}>{done ? '✓' : ''}</span>
    <span className={done ? 'text-slate-700' : 'text-slate-400'}>{label}</span>
    {note && <span className="ml-auto text-xs text-slate-400">{note}</span>}
  </li>
);

const Summary = ({ label, value }) => (
  <div className="flex justify-between border-b border-slate-100 pb-1.5"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-800">{value}</span></div>
);

export default DevelopmentReportBuilder;
